import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowDownToLine, Send, Wallet, Users, Repeat, Link2,
  Globe2, Cable, Eye, TrendingUp, Building2, Layers, Store, Landmark,
  ShieldCheck, Lock, Activity, FileCheck, Code2, Webhook, KeyRound, BookOpen,
} from "lucide-react";
import Reveal from "../components/Reveal";
import HeroVisual from "../components/home/HeroVisual";
import MoneyFlowDiagram from "../components/home/MoneyFlowDiagram";
import GlobalMovement from "../components/home/GlobalMovement";
import CorridorRow from "../components/home/CorridorRow";
import GlobeHero from "../components/home/GlobeHero";
import DashboardMockup from "../components/home/DashboardMockup";
import CodeEditor from "../components/home/CodeEditor";
import CtaSection from "../components/CtaSection";
import FxTicker from "../components/FxTicker";

const products = [
  { icon: ArrowDownToLine, title: "Receive Payments", tagline: "Get paid globally.", desc: "Accept money from customers through supported payment methods, bank transfers and digital payment channels.", cta: "Explore Payments", to: "/products#payments" },
  { icon: Send, title: "Send Money", tagline: "Move money wherever business takes you.", desc: "Send domestic and international payments securely to supported bank accounts and payment destinations.", cta: "Explore Transfers", to: "/products#payouts" },
  { icon: Wallet, title: "Multi-Currency Accounts", tagline: "Manage multiple currencies from one place.", desc: "Hold and manage supported currencies with complete visibility into balances and transactions.", cta: "Explore Accounts", to: "/products#accounts" },
  { icon: Users, title: "Global Payouts", tagline: "Pay customers, partners and suppliers.", desc: "Make individual or bulk payouts across supported markets through one connected platform.", cta: "Explore Payouts", to: "/products#payouts" },
  { icon: Repeat, title: "Foreign Exchange", tagline: "Convert currencies with confidence.", desc: "Manage currency conversion as part of your global money movement workflow.", cta: "Explore FX", to: "/products#fx" },
  { icon: Link2, title: "Payment Links", tagline: "Get paid with a link.", desc: "Create secure payment links and share them through digital channels without building a custom checkout experience.", cta: "Explore Payment Links", to: "/products#payment-links" },
];

const solutions = [
  { icon: Globe2, title: "Global Businesses", desc: "Expand into new markets without rebuilding your payment stack.", to: "/solutions#global-businesses" },
  { icon: Layers, title: "Fintechs", desc: "Build financial experiences with connected payment infrastructure.", to: "/solutions#fintechs" },
  { icon: Send, title: "Remittance Companies", desc: "Simplify cross-border money movement and settlement.", to: "/solutions#remittance" },
  { icon: Store, title: "Marketplaces", desc: "Collect payments and move funds between customers, sellers and partners.", to: "/solutions#marketplaces" },
  { icon: Building2, title: "Platforms", desc: "Embed payments and money movement directly into your product.", to: "/solutions#platforms" },
  { icon: Landmark, title: "Enterprises", desc: "Centralize collections, payouts, transfers and financial operations.", to: "/solutions#enterprises" },
];

const securityCards = [
  { icon: Lock, title: "Secure by Design", desc: "Protect sensitive information and transactions with modern security practices." },
  { icon: Eye, title: "Control & Visibility", desc: "Monitor account activity, payments and transfers from one centralized platform." },
  { icon: Activity, title: "Operational Reliability", desc: "Infrastructure designed to support dependable money movement." },
  { icon: FileCheck, title: "Compliance Ready", desc: "Designed to support the controls and processes required for modern financial services." },
];

const steps = [
  { n: "01", title: "Connect", desc: "Connect your business or product to Swiftwire." },
  { n: "02", title: "Move", desc: "Collect, convert, send and manage money through one platform." },
  { n: "03", title: "Grow", desc: "Expand into new markets with connected payment infrastructure." },
];

const whyItems = [
  { icon: Cable, title: "One Connected Platform", desc: "Payments, accounts, FX and payouts working together." },
  { icon: Globe2, title: "Global by Design", desc: "Built for businesses operating across borders and markets." },
  { icon: Code2, title: "Developer First", desc: "Modern APIs and tools designed for efficient integration." },
  { icon: TrendingUp, title: "Built for Growth", desc: "Infrastructure designed to grow with your transaction volume and business." },
];

const trustItems = [
  { icon: Globe2, label: "African Reach" },
  { icon: Cable, label: "Connected Infrastructure" },
  { icon: Eye, label: "Real-Time Visibility" },
  { icon: TrendingUp, label: "Built for Scale" },
];

const devFeatures = ["REST APIs", "Secure authentication", "Webhooks", "Sandbox environment", "Transaction status", "API documentation", "Developer tools"];

const Home = () => (
  <div data-testid="home-page">
    {/* SECTION 1 — HERO */}
    <section className="sw-grid-bg relative overflow-hidden">
      <div className="sw-glow pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px]" aria-hidden="true" />
      <div
        className="pointer-events-none absolute left-1/2 top-[64%] -translate-x-1/2 -translate-y-1/2 lg:left-auto lg:right-[-90px] lg:top-1/2 lg:translate-x-0"
        aria-hidden="true"
      >
        <GlobeHero />
      </div>
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-20 pt-16 sm:px-6 md:pb-28 md:pt-24 lg:grid-cols-2 lg:gap-10 lg:px-8">
        <div>
          <p className="sw-rise sw-rise-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#2E7CD6]" data-testid="hero-eyebrow">
            Global Payments • Money Movement • Financial Infrastructure
          </p>
          <h1 className="sw-rise sw-rise-2 mt-5 font-heading text-[40px] font-extrabold leading-[1.06] tracking-tight text-[#0F172A] sm:text-5xl lg:text-[64px]" data-testid="hero-headline">
            Move money without borders.
          </h1>
          <p className="sw-rise sw-rise-3 mt-6 max-w-xl text-base leading-relaxed text-[#334155] md:text-lg" data-testid="hero-subtext">
            One powerful platform to collect, hold, convert and send money across Africa and beyond. Swiftwire makes money movement simpler, faster and more connected.
          </p>
          <div className="sw-rise sw-rise-4 mt-9 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/contact"
              data-testid="hero-get-started"
              className="group flex items-center justify-center gap-2 rounded-full bg-[#1E3A6E] px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#16294e]"
            >
              Get Started
              <ArrowRight size={16} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-talk-to-sales"
              className="flex items-center justify-center rounded-full border border-[#E2E8F0] bg-white px-8 py-3.5 text-sm font-semibold text-[#0F172A] transition-colors hover:bg-slate-50"
            >
              Talk to Sales
            </Link>
          </div>
          <div className="sw-rise sw-rise-4 mt-7 flex items-center gap-3 sm:gap-3.5" data-testid="hero-faces">
            <div className="flex -space-x-3 sm:-space-x-2.5">
              {[
                { img: "photo-1573164713988-8665fc963095", alt: "Business owner" },
                { img: "photo-1531384441138-2736e62e0919", alt: "Finance professional" },
                { img: "photo-1531123897727-8f129e1688ce", alt: "Product founder" },
                { img: "photo-1531482615713-2afd69097998", alt: "Developer" },
              ].map((a) => (
                <img
                  key={a.img}
                  src={`https://images.unsplash.com/${a.img}?w=96&h=96&q=70&auto=format&fit=crop&crop=faces`}
                  alt={a.alt}
                  loading="lazy"
                  className="h-8 w-8 rounded-full border-2 border-white object-cover shadow-sm sm:h-9 sm:w-9"
                />
              ))}
            </div>
            <p className="text-xs leading-snug text-[#64748B] sm:text-sm">Join the businesses moving money with Swiftwire</p>
          </div>
          <p className="sw-rise sw-rise-5 mt-7 flex items-center gap-2 text-sm text-[#64748B]">
            <ShieldCheck size={16} strokeWidth={1.5} className="text-[#2E7CD6]" />
            Built for businesses moving money across Africa and beyond.
          </p>
          <div className="sw-rise sw-rise-5">
            <FxTicker />
          </div>
        </div>
        <div className="sw-rise sw-rise-3 py-8 lg:py-0">
          <HeroVisual />
        </div>
      </div>
    </section>

    {/* SECTION 2 — TRUST STRIP */}
    <section className="border-y border-[#E2E8F0] bg-white py-12" data-testid="trust-strip">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="text-center font-heading text-lg font-semibold text-[#0F172A]">Everything you need to move money globally.</p>
          <div className="mt-8 grid grid-cols-2 gap-6 md:grid-cols-4">
            {trustItems.map((t) => (
              <div key={t.label} className="flex items-center justify-center gap-2.5 text-[#334155]">
                <t.icon size={18} strokeWidth={1.5} className="text-[#2E7CD6]" />
                <span className="text-sm font-semibold">{t.label}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>

    {/* SECTION 3 — ONE CONNECTED PLATFORM */}
    <section className="bg-white py-20 md:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#2E7CD6]">One Connected Platform</p>
          <h2 className="mt-4 font-heading text-4xl font-bold tracking-tight text-[#0F172A] md:text-5xl">One platform. Every way money moves.</h2>
          <p className="mt-5 text-base leading-relaxed text-[#334155] md:text-lg">
            From collecting payments to managing balances, converting currencies and sending funds across borders, Swiftwire brings the complete money movement journey together in one platform.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="mt-12 md:mt-16">
          <MoneyFlowDiagram />
        </Reveal>
      </div>
    </section>

    {/* SECTION 4 — PRODUCTS */}
    <section className="bg-[#F8FAFC] py-20 md:py-32" data-testid="products-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <h2 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A] md:text-5xl">Everything you need to move money.</h2>
          <p className="mt-5 text-base text-[#334155] md:text-lg">Powerful payment and money movement capabilities, connected through one platform.</p>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 md:mt-16">
          {products.map((p, i) => (
            <Reveal key={p.title} delay={(i % 3) * 0.08}>
              <Link
                to={p.to}
                data-testid={`product-card-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group flex h-full flex-col rounded-2xl border border-[#E2E8F0] bg-white p-7 shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-900/[0.06]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EFF5FD] text-[#1E3A6E]">
                  <p.icon size={20} strokeWidth={1.5} />
                </span>
                <h3 className="mt-5 font-heading text-xl font-bold text-[#0F172A]">{p.title}</h3>
                <p className="mt-1.5 text-sm font-semibold text-[#2E7CD6]">{p.tagline}</p>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-[#64748B]">{p.desc}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E3A6E]">
                  {p.cta}
                  <ArrowRight size={15} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* SECTION 5 — GLOBAL MONEY MOVEMENT VISUAL */}
    <section className="sw-grid-bg border-y border-[#E2E8F0] bg-[#F1F5F9]/60 py-20 md:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <h2 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A] md:text-5xl">From payment received to money delivered.</h2>
        </Reveal>
        <div className="mt-14">
          <GlobalMovement />
          <CorridorRow />
        </div>
      </div>
    </section>

    {/* SECTION 6 — DASHBOARD */}
    <section className="bg-white py-20 md:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <h2 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A] md:text-5xl">Complete visibility. Total control.</h2>
          <p className="mt-5 text-base text-[#334155] md:text-lg">
            See your balances, payments, transfers and transaction activity from a single, intuitive workspace.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="mt-12 md:mt-16">
          <DashboardMockup />
          <p className="mt-5 text-center text-xs text-[#94A3B8]">Illustrative product interface. Figures shown are sample UI data, not live Swiftwire statistics.</p>
        </Reveal>
      </div>
    </section>

    {/* SECTION 7 — BUSINESS SOLUTIONS */}
    <section className="bg-[#F8FAFC] py-20 md:py-32" data-testid="solutions-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#2E7CD6]">Built for the Way Business Moves</p>
          <h2 className="mt-4 font-heading text-4xl font-bold tracking-tight text-[#0F172A] md:text-5xl">Payments infrastructure for a global world.</h2>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 md:mt-16">
          {solutions.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.08}>
              <Link
                to={s.to}
                data-testid={`solution-card-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group flex h-full flex-col rounded-2xl border border-[#E2E8F0] bg-white p-7 shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-900/[0.06]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EFF5FD] text-[#1E3A6E]">
                  <s.icon size={20} strokeWidth={1.5} />
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-[#0F172A]">{s.title}</h3>
                <p className="mt-2.5 flex-1 text-sm leading-relaxed text-[#64748B]">{s.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E3A6E]">
                  Learn more
                  <ArrowRight size={15} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* SECTION 7.5 — PEOPLE */}
    <section className="bg-white py-20 md:py-32" data-testid="people-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <h2 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A] md:text-5xl">Built for the people who move money.</h2>
          <p className="mt-5 text-base text-[#334155] md:text-lg">
            Behind every payment is a person building something. Swiftwire is designed around the people who collect, send and manage money every day.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 md:mt-16">
          {[
            { img: "photo-1531384441138-2736e62e0919", alt: "A smiling founder in a colourful shirt", caption: "Founders collecting payments worldwide" },
            { img: "photo-1573164713988-8665fc963095", alt: "A professional African woman working with a tablet", caption: "Finance teams paying partners globally" },
            { img: "photo-1531123897727-8f129e1688ce", alt: "A professional African woman in a white shirt", caption: "Developers building modern money apps" },
          ].map((p, i) => (
            <Reveal key={p.img} delay={i * 0.1}>
              <figure className="group overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white shadow-sm">
                <div className="h-64 overflow-hidden">
                  <img
                    src={`https://images.unsplash.com/${p.img}?w=800&h=560&q=80&auto=format&fit=crop&crop=faces`}
                    alt={p.alt}
                    loading="lazy"
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                </div>
                <figcaption className="flex min-h-[54px] items-center px-5 py-4 text-sm font-medium text-[#334155]">{p.caption}</figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* SECTION 8 — DEVELOPER (dark panel) */}
    <section className="bg-[#0B1120] py-20 md:py-32" data-testid="developer-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <Reveal>
          <h2 className="font-heading text-4xl font-bold tracking-tight text-white md:text-5xl">Build on Swiftwire.</h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-300 md:text-lg">
            Power your products with APIs designed for modern payments and money movement.
          </p>
          <h3 className="mt-10 font-heading text-lg font-semibold text-white">Developer-friendly infrastructure</h3>
          <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {devFeatures.map((f) => (
              <li key={f} className="flex items-center gap-2.5 text-sm text-slate-300">
                <span className="flex h-6 w-6 items-center justify-center rounded-md bg-[#2E7CD6]/15 text-[#6BA3E8]">
                  {f === "REST APIs" && <Code2 size={13} strokeWidth={1.5} />}
                  {f === "Secure authentication" && <KeyRound size={13} strokeWidth={1.5} />}
                  {f === "Webhooks" && <Webhook size={13} strokeWidth={1.5} />}
                  {f === "Sandbox environment" && <Layers size={13} strokeWidth={1.5} />}
                  {f === "Transaction status" && <Activity size={13} strokeWidth={1.5} />}
                  {f === "API documentation" && <BookOpen size={13} strokeWidth={1.5} />}
                  {f === "Developer tools" && <Cable size={13} strokeWidth={1.5} />}
                </span>
                {f}
              </li>
            ))}
          </ul>
          <Link
            to="/developers"
            data-testid="developer-explore-cta"
            className="group mt-9 inline-flex items-center gap-2 rounded-full bg-[#2E7CD6] px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#2568b8]"
          >
            Explore Developer APIs
            <ArrowRight size={16} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-0.5" />
          </Link>
          <p className="mt-5 text-xs text-slate-500">Platform capabilities shown as planned developer offering.</p>
        </Reveal>
        <Reveal delay={0.15}>
          <CodeEditor />
          <FxTicker dark />
        </Reveal>
      </div>
    </section>

    {/* SECTION 9 — SECURITY */}
    <section className="bg-white py-20 md:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <h2 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A] md:text-5xl">Your money deserves serious infrastructure.</h2>
          <p className="mt-5 text-base text-[#334155] md:text-lg">
            Security and operational control are built into every part of the Swiftwire experience.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4 md:mt-16">
          {securityCards.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-[#E2E8F0] bg-white p-7 shadow-sm" data-testid={`security-card-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EFF5FD] text-[#1E3A6E]">
                  <c.icon size={20} strokeWidth={1.5} />
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-[#0F172A]">{c.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-[#64748B]">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* SECTION 10 — SIMPLE EXPERIENCE */}
    <section className="bg-[#F8FAFC] py-20 md:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <h2 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A] md:text-5xl">Global payments shouldn't feel complicated.</h2>
        </Reveal>
        <div className="relative mt-14 grid gap-10 md:grid-cols-3 md:gap-6">
          <div className="absolute left-[16%] right-[16%] top-8 hidden h-px bg-[#DBE4F0] md:block" aria-hidden="true" />
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.12}>
              <div className="relative flex flex-col items-center text-center" data-testid={`step-${s.n}`}>
                <span className="relative z-10 flex h-16 w-16 items-center justify-center rounded-2xl border border-[#E2E8F0] bg-white font-heading text-lg font-extrabold text-[#2E7CD6] shadow-sm">
                  {s.n}
                </span>
                <h3 className="mt-5 font-heading text-xl font-bold text-[#0F172A]">{s.title}</h3>
                <p className="mt-2.5 max-w-xs text-sm leading-relaxed text-[#64748B]">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* SECTION 11 — WHY SWIFTWIRE */}
    <section className="bg-white py-20 md:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <h2 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A] md:text-5xl">Why businesses choose Swiftwire.</h2>
        </Reveal>
        <div className="mt-12 grid gap-x-10 gap-y-12 sm:grid-cols-2 md:mt-16">
          {whyItems.map((w, i) => (
            <Reveal key={w.title} delay={(i % 2) * 0.1}>
              <div className="flex gap-5" data-testid={`why-${w.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#1E3A6E] to-[#2E7CD6] text-white shadow-md shadow-[#2E7CD6]/20">
                  <w.icon size={20} strokeWidth={1.5} />
                </span>
                <div>
                  <h3 className="font-heading text-xl font-bold text-[#0F172A]">{w.title}</h3>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-[#64748B] md:text-base">{w.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* SECTION 12 — FINAL CTA */}
    <CtaSection />
  </div>
);

export default Home;
